/**
 * StateIntelligenceCard — collapsible reference card showing the full state
 * law entry (Phase 9A data) + strategy notes + citations + all applicable
 * warnings from the warnings engine.
 *
 * Default state: collapsed. GET /api/judgment-law/rules/:variant_key.
 */
import React, { useEffect, useState } from "react";
import { BookOpen, ChevronDown, ExternalLink, ShieldAlert } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { apiClient } from "@/lib/apiClient";
import { useJudgmentTimeline } from "@/hooks/useJudgmentLaw";
import JeaCard from "@/components/jea/JeaCard";
import { cn } from "@/lib/utils";

const InfoRow = ({ label, value, mono }) => (
  <div className="flex items-start justify-between gap-4 py-1.5 border-b border-border-default/40 last:border-0">
    <div className="text-[12px] text-text-secondary">{label}</div>
    <div className={cn("text-[13px] text-white text-right max-w-[65%]", mono && "jea-mono")}>
      {value ?? "—"}
    </div>
  </div>
);

const severityBg = {
  emergency: "border-danger bg-danger/10 text-danger",
  critical: "border-danger bg-danger/10 text-danger",
  urgent: "border-warning bg-warning/10 text-warning",
  recommended: "border-accent-pink bg-accent-pink/10 text-accent-pink",
  info: "border-border-default bg-bg-tertiary text-text-secondary",
};

export const StateIntelligenceCard = ({ variantKey, payload, testid = "state-intel-card" }) => {
  const [rule, setRule] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { data: timeline } = useJudgmentTimeline(payload);

  useEffect(() => {
    if (!variantKey || rule) return;
    setLoading(true);
    apiClient
      .get(`/judgment-law/rules/${variantKey}`)
      .then(({ data }) => setRule(data))
      .catch(() => setRule(null))
      .finally(() => setLoading(false));
  }, [variantKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!variantKey) return null;

  return (
    <JeaCard className="p-0 overflow-hidden" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-bg-tertiary/60 transition-colors"
        data-testid={`${testid}-toggle`}
      >
        <div className="flex items-center gap-3">
          <BookOpen size={18} className="text-accent-pink" />
          <div>
            <div className="text-white font-semibold text-[15px]">
              State Reference — {rule?.name || "…"}
            </div>
            {rule?.special_flags?.length > 0 && (
              <div className="text-[11px] text-text-secondary mt-0.5">
                {rule.special_flags.join(" · ")}
              </div>
            )}
          </div>
        </div>
        <ChevronDown
          size={16}
          className={cn(
            "text-text-secondary transition-transform duration-200",
            open && "rotate-180"
          )}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && rule && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-5 space-y-4 border-t border-border-default">
              <div className="pt-4">
                <InfoRow label="Judgment duration" value={rule.duration_note} />
                <InfoRow label="Dormancy trigger" value={rule.dormancy_years ? `${rule.dormancy_years} years` : "None"} />
                <InfoRow label="Renewal mechanism" value={rule.renewal_mechanism} />
                <InfoRow label="Lien life" value={rule.lien_life_note} />
                <InfoRow
                  label="Lien differs from judgment"
                  value={rule.lien_differs_from_judgment ? "Yes — track separately" : "No — same clock"}
                />
                <InfoRow label="Hard cutoff" value={rule.hard_cutoff ? "Yes — no renewal path" : "No"} />
                {rule.expired && (
                  <InfoRow label="Status" value="Permanently expired" />
                )}
              </div>

              {rule.strategy_note && (
                <div className="rounded-[8px] border border-accent-pink/40 bg-accent-pink/5 p-3">
                  <div className="jea-caption text-accent-pink mb-1">Strategy Note</div>
                  <div className="text-[13px] text-text-secondary">{rule.strategy_note}</div>
                </div>
              )}

              {rule.citation && (
                <div>
                  <div className="jea-caption mb-1">Citations</div>
                  <a
                    href={rule.citation}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-accent-pink hover:text-accent-pink-light text-[12px] jea-mono"
                    data-testid={`${testid}-citation`}
                  >
                    <ExternalLink size={11} />
                    {rule.citation}
                  </a>
                </div>
              )}

              {(timeline?.warnings || []).length > 0 && (
                <div>
                  <div className="jea-caption mb-2">Applicable Warnings</div>
                  <div className="space-y-2">
                    {timeline.warnings.map((w, i) => (
                      <div
                        key={i}
                        className={cn(
                          "flex items-start gap-2 rounded-[8px] border p-2 text-[12px]",
                          severityBg[w.severity] || severityBg.info,
                        )}
                        data-testid={`state-intel-warning-${w.type}`}
                      >
                        <ShieldAlert size={12} className="shrink-0 mt-0.5" />
                        <div>
                          <div className="font-bold uppercase tracking-[0.06em] text-[9px]">{w.severity}</div>
                          <div className="text-white">{w.message}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-2 text-[11px] text-text-muted italic border-t border-border-default">
                Consult local counsel — this is a reference summary, not legal advice.
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </JeaCard>
  );
};

export default StateIntelligenceCard;
